import { useState, useEffect } from 'react';

import { apiClient, createAuthHeaders } from '@/lib/api-client';
import { firebaseAuth } from '@/lib/firebase';

// In-memory cache (shared across all hook instances)
let cachedValue: boolean | null = null;
let fetchPromise: Promise<boolean> | null = null;

async function fetchIsPlatformAdmin(): Promise<boolean> {
  const user = firebaseAuth.currentUser;
  if (!user) return false;

  const token = await user.getIdToken();
  const response = await apiClient.get('/api/auth/me', {
    headers: createAuthHeaders(token),
  });
  return response.data?.user?.isPlatformAdmin === true;
}

/**
 * Hook to check if the current user is a platform admin.
 * Fetches /auth/me once and caches the result in memory.
 */
export function usePlatformAdmin(): { isPlatformAdmin: boolean; loading: boolean } {
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(cachedValue ?? false);
  const [loading, setLoading] = useState(cachedValue === null);

  useEffect(() => {
    if (cachedValue !== null) {
      setIsPlatformAdmin(cachedValue);
      setLoading(false);
      return;
    }

    if (fetchPromise) {
      fetchPromise
        .then((value) => {
          setIsPlatformAdmin(value);
          setLoading(false);
        })
        .catch(() => {
          setIsPlatformAdmin(false);
          setLoading(false);
        });
      return;
    }

    fetchPromise = fetchIsPlatformAdmin();
    fetchPromise
      .then((value) => {
        cachedValue = value;
        setIsPlatformAdmin(value);
        setLoading(false);
      })
      .catch(() => {
        setIsPlatformAdmin(false);
        setLoading(false);
      })
      .finally(() => {
        fetchPromise = null;
      });
  }, []);

  return { isPlatformAdmin, loading };
}
